import { useState, useRef, useEffect } from 'react'
import { useQuery, useMutation } from '@tanstack/react-query'
import { practiceAPI } from '../../api/client'
import { useExamFramework } from '../../hooks/useExamFramework'
import InteractiveTranscript from '../../components/InteractiveTranscript'
import AppLayout from '../../components/layout/AppLayout'

const H = ({ style, ...p }: React.HTMLAttributes<HTMLElement>) => (
  <span style={{ fontFamily: "'Space Grotesk', sans-serif", ...style }} {...p} />
)

/** FR-008 Listening Training Module — parts rendered dynamically per exam (FR-029) */
export default function ListeningPage() {
  const { parts, partLabel, displayName, examType, isLoading } = useExamFramework()
  const listeningParts = parts('listening')
  const [activePart, setActivePart] = useState<string>('')

  // Default to the first part once the framework loads
  useEffect(() => {
    if (!activePart && listeningParts.length > 0) setActivePart(listeningParts[0].partCode)
  }, [listeningParts, activePart])

  return (
    <AppLayout>
      {/* Header */}
      <div style={{ marginBottom: '2rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
          <span className="material-symbols-outlined" style={{ color: '#006571', fontSize: '1.125rem', fontVariationSettings: "'FILL' 1" }}>headphones</span>
          <span style={{ fontSize: '0.6875rem', fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#006571' }}>Listening</span>
        </div>
        <H style={{ display: 'block', fontSize: 'clamp(1.75rem, 3vw, 2.5rem)', fontWeight: 900, letterSpacing: '-0.03em', marginBottom: '0.5rem' }}>
          Luyện nghe <span style={{ color: '#006571', fontStyle: 'italic' }}>{displayName}</span>
        </H>
        <p style={{ color: '#535b71', fontSize: '1rem', lineHeight: 1.6 }}>
          {listeningParts.length} phần nghe chuẩn {displayName}. Mỗi đoạn nghe theo đúng quy định thi.
        </p>
      </div>

      {/* Part tabs (data-driven) */}
      {isLoading || listeningParts.length === 0 ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '4rem' }}><div className="spinner" /></div>
      ) : (
        <>
          <div className="scroll-tabs" style={{ display: 'flex', gap: '0.75rem', marginBottom: '2rem' }}>
            {listeningParts.map(part => {
              const sel = activePart === part.partCode
              return (
                <button
                  key={part.partCode}
                  onClick={() => setActivePart(part.partCode)}
                  style={{
                    flex: 1, minWidth: 0,
                    padding: '1rem 1.25rem', borderRadius: '1rem', border: sel ? 'none' : '1.5px solid #e8ecf8',
                    cursor: 'pointer', transition: 'all 0.2s', textAlign: 'left',
                    background: sel ? 'linear-gradient(135deg, #006571, #00e3fd)' : 'white',
                    boxShadow: sel ? '0 8px 20px rgba(0,101,113,0.25)' : '0 2px 8px rgba(0,0,0,0.04)',
                  }}
                >
                  <H style={{ display: 'block', fontSize: '0.9375rem', fontWeight: 800, color: sel ? 'white' : '#272e42', lineHeight: 1.3, marginBottom: '0.375rem' }}>{partLabel(part)}</H>
                  <span style={{ fontSize: '0.75rem', color: sel ? 'rgba(255,255,255,0.6)' : '#a5adc6', fontWeight: 600 }}>{part.questionCount ? `${part.questionCount} câu` : ''}</span>
                </button>
              )
            })}
          </div>

          {activePart && <ListeningExercise skill="listening" part={activePart} examType={examType} key={activePart} />}
        </>
      )}
    </AppLayout>
  )
}

// ─── Listening Exercise ───────────────────────────────────────────────────────

function ListeningExercise({ skill, part, examType }: { skill: string; part: string; examType: string }) {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['practice', skill, part, examType],
    queryFn: () => practiceAPI.getQuestions(skill, part, examType),
  })

  const [answers, setAnswers] = useState<Record<string, string>>({})
  const [results, setResults] = useState<Record<string, { isCorrect: boolean | null; correctAnswer: string; explanation: string }>>({})
  const [submitted, setSubmitted] = useState(false)
  const [playCount, setPlayCount] = useState(0)
  const audioRef = useRef<HTMLAudioElement>(null)

  const submitAnswer = useMutation({
    mutationFn: ({ questionId, answer }: { questionId: string; answer: string }) =>
      practiceAPI.submitAnswer(questionId, answer, 0),
  })

  if (isLoading) return (
    <div style={{ display: 'flex', justifyContent: 'center', padding: '4rem' }}>
      <div className="spinner" />
    </div>
  )
  if (error) return (
    <div style={{ padding: '1.5rem', background: 'rgba(180,19,64,0.08)', borderRadius: '1rem', color: '#b41340', fontWeight: 600 }}>
      Không thể tải bài tập. Vui lòng thử lại.
    </div>
  )

  const { content, questions } = data ?? {}

  if (!questions || questions.length === 0) return (
    <div style={{ textAlign: 'center', padding: '4rem', background: 'white', borderRadius: '1.5rem' }}>
      <span className="material-symbols-outlined" style={{ fontSize: '3rem', color: '#a5adc6', display: 'block', marginBottom: '1rem' }}>pending</span>
      <H style={{ display: 'block', fontWeight: 700, color: '#535b71', marginBottom: '0.5rem' }}>Nội dung bài nghe đang được cập nhật</H>
      <p style={{ color: '#a5adc6', fontSize: '0.875rem' }}>Vui lòng quay lại sau.</p>
    </div>
  )

  async function handleSubmitAll() {
    const newResults: typeof results = {}
    for (const q of questions!) {
      const answer = answers[q.id] ?? ''
      if (!answer) continue
      const res = await submitAnswer.mutateAsync({ questionId: q.id, answer })
      newResults[q.id] = res
    }
    setResults(newResults)
    setSubmitted(true)
  }

  const allAnswered = questions.every((q: any) => answers[q.id])
  const score = Object.values(results).filter(r => r.isCorrect).length

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      {/* Audio player */}
      {content?.url && (
        <div className="audio-player-card" style={{ background: '#060e20', borderRadius: '1.5rem', color: 'white' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
            <div>
              {content.title && <H style={{ display: 'block', fontWeight: 800, fontSize: '1.0625rem', marginBottom: '0.25rem' }}>{content.title}</H>}
              <span style={{ fontSize: '0.75rem', color: '#809bff', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase' }}>Listening</span>
            </div>
            <div style={{ padding: '0.5rem 1rem', borderRadius: '9999px', background: playCount >= 2 ? 'rgba(180,19,64,0.3)' : 'rgba(0,75,226,0.3)', fontSize: '0.8125rem', fontWeight: 700, color: playCount >= 2 ? '#f74b6d' : '#809bff' }}>
              {playCount}/2 lần nghe
            </div>
          </div>
          <audio
            ref={audioRef}
            src={content.url}
            controls
            onPlay={() => {
              const audio = audioRef.current
              if (!audio) return
              // Only count a new listen when starting from near the beginning
              // (not when resuming after pause mid-playback)
              const isNewListen = audio.currentTime < 1
              if (isNewListen) {
                if (playCount >= 2) {
                  audio.pause()
                  audio.currentTime = 0
                  return
                }
                setPlayCount(c => c + 1)
              } else if (playCount >= 2) {
                audio.pause()
              }
            }}
            style={{ width: '100%', borderRadius: '0.75rem', opacity: playCount >= 2 ? 0.5 : 1, pointerEvents: playCount >= 2 ? 'none' : 'auto' }}
          />
          {playCount >= 2 && (
            <p style={{ marginTop: '0.75rem', fontSize: '0.8125rem', color: '#f74b6d', fontWeight: 600 }}>
              ⚠ Đã hết lượt nghe (tối đa 2 lần, đúng quy định thi).
            </p>
          )}
        </div>
      )}

      {/* Questions */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.875rem' }}>
        {questions.map((q: any, idx: number) => (
          <QuestionCard
            key={q.id}
            index={idx + 1}
            question={q}
            selectedAnswer={answers[q.id]}
            result={submitted ? results[q.id] : undefined}
            onAnswer={ans => setAnswers(p => ({ ...p, [q.id]: ans }))}
            disabled={submitted}
          />
        ))}
      </div>

      {/* Submit / Result */}
      {!submitted ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', padding: '1.5rem', background: 'white', borderRadius: '1.5rem', boxShadow: '0 4px 16px rgba(0,0,0,0.04)' }}>
          <span style={{ fontSize: '0.875rem', color: '#535b71', fontWeight: 600 }}>
            {Object.keys(answers).length}/{questions.length} câu đã trả lời
          </span>
          <button
            onClick={handleSubmitAll}
            disabled={!allAnswered || submitAnswer.isPending}
            style={{
              marginLeft: 'auto', padding: '0.875rem 2rem', borderRadius: '9999px', border: 'none',
              cursor: allAnswered ? 'pointer' : 'not-allowed',
              background: allAnswered ? 'linear-gradient(135deg, #004be2, #809bff)' : '#d1dcff',
              color: allAnswered ? 'white' : '#a5adc6',
              fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700,
            }}
          >
            {submitAnswer.isPending ? 'Đang chấm...' : 'Nộp bài'}
          </button>
        </div>
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '1.5rem', background: 'white', borderRadius: '1.5rem', boxShadow: '0 4px 16px rgba(0,0,0,0.04)', flexWrap: 'wrap', gap: '1rem' }}>
          <div>
            <H style={{ display: 'block', fontSize: '1.25rem', fontWeight: 900, color: '#004be2' }}>{score}/{questions.length}</H>
            <span style={{ fontSize: '0.8125rem', color: '#535b71' }}>câu đúng</span>
          </div>
          <button
            onClick={() => { refetch(); setAnswers({}); setResults({}); setSubmitted(false); setPlayCount(0) }}
            style={{ padding: '0.875rem 1.75rem', borderRadius: '9999px', border: 'none', background: '#060e20', color: 'white', fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700, cursor: 'pointer' }}
          >
            Bài tiếp theo →
          </button>
        </div>
      )}

      {/* Interactive Transcript (FR-005) */}
      {submitted && content?.transcript && (
        <div style={{ background: '#eef0ff', borderRadius: '1.5rem', padding: '2rem' }}>
          <H style={{ display: 'block', fontWeight: 800, marginBottom: '1rem' }}>Transcript</H>
          <InteractiveTranscript
            text={content.transcript}
            contentId={content.id ?? ''}
            audioRef={audioRef}
            audioDuration={content.durationSeconds ?? undefined}
          />
        </div>
      )}
    </div>
  )
}

// ─── Question Card ────────────────────────────────────────────────────────────

function QuestionCard({ index, question, selectedAnswer, result, onAnswer, disabled }: {
  index: number; question: any; selectedAnswer?: string
  result?: { isCorrect: boolean | null; correctAnswer: string; explanation: string }
  onAnswer: (ans: string) => void; disabled: boolean
}) {
  return (
    <div style={{ background: 'white', borderRadius: '1.25rem', padding: '1.5rem', boxShadow: '0 4px 12px rgba(0,0,0,0.04)' }}>
      <p style={{ fontSize: '0.9375rem', fontWeight: 600, color: '#272e42', marginBottom: '1rem', lineHeight: 1.6 }}>
        <strong style={{ color: '#006571' }}>Câu {index}:</strong> {question.prompt}
      </p>
      {question.options ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {question.options.map((opt: string, i: number) => {
            const label = String.fromCharCode(65 + i)
            // Strip existing "A. " prefix from DB if present, to avoid double-prefix
            const displayText = opt.replace(/^[A-Da-d][.\s]\s*/, '')
            const isSel = selectedAnswer === label
            const isCorrectOpt = result?.correctAnswer === label
            let bg = 'transparent', border = '1.5px solid #d1dcff', color = '#272e42'
            if (result) {
              if (isCorrectOpt) { bg = 'rgba(0,101,113,0.08)'; border = '1.5px solid #006571'; color = '#006571' }
              else if (isSel && !result.isCorrect) { bg = 'rgba(180,19,64,0.06)'; border = '1.5px solid #b41340'; color = '#b41340' }
            } else if (isSel) { bg = 'rgba(0,101,113,0.06)'; border = '1.5px solid rgba(0,101,113,0.4)' }
            return (
              <button key={i} onClick={() => !disabled && onAnswer(label)} disabled={disabled}
                style={{ width: '100%', textAlign: 'left', padding: '0.875rem 1rem', borderRadius: '0.875rem', background: bg, border, color, cursor: disabled ? 'default' : 'pointer', fontSize: '0.9rem', fontWeight: 500, transition: 'all 0.2s', display: 'flex', alignItems: 'center', gap: '0.875rem' }}>
                <span style={{ fontFamily: "'Space Grotesk', sans-serif", fontWeight: 800, fontSize: '0.875rem', opacity: 0.6 }}>{label}.</span>
                {displayText}
              </button>
            )
          })}
        </div>
      ) : (
        <input type="text" value={selectedAnswer ?? ''} onChange={e => onAnswer(e.target.value)} disabled={disabled} placeholder="Nhập câu trả lời..."
          style={{ width: '100%', padding: '0.75rem 1rem', borderRadius: '0.75rem', background: '#eef0ff', border: 'none', outline: 'none', fontFamily: "'Manrope', sans-serif", fontSize: '0.9375rem' }} />
      )}
      {result && (
        <p style={{ marginTop: '0.75rem', fontSize: '0.8125rem', fontWeight: 600, color: result.isCorrect ? '#006571' : '#b41340', lineHeight: 1.5 }}>
          {result.isCorrect ? '✓ Đúng!' : `✗ Đáp án: ${result.correctAnswer}`}
          {result.explanation && <span style={{ display: 'block', fontWeight: 400, color: '#535b71' }}>{result.explanation}</span>}
        </p>
      )}
    </div>
  )
}
